import { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Paper, IconButton, TextField, Typography, Fab } from '@mui/material'
import { Close, Refresh, Send, AutoAwesome, Mic, Stop } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigateWithQueryParams } from '../Utilities'
import * as ChatManager from './ChatManager'

const SPEECH_LANGS = { de: 'de-DE', en: 'en-US', nl: 'nl-NL' }

export default function ChatWidget() {

    const { t, i18n } = useTranslation()
    const navigate = useNavigateWithQueryParams()

    // register navigate function so ChatManager can navigate from anywhere
    useEffect(() => {
        ChatManager.setNavigate(navigate)
        return () => ChatManager.setNavigate(null)
    }, [navigate])

    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState([{ role: 'assistant', text: t('chatbot.greeting') }])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [chatMode, setChatMode] = useState(ChatManager.getMode())
    const [isListening, setIsListening] = useState(false)
    const [speechSupported, setSpeechSupported] = useState(false)

    const recognitionRef = useRef(null)
    const messagesEndRef = useRef(null)
    const initDone = useRef(false)

    // init chatbot mode detection once on first open
    useEffect(() => {
        if (isOpen && !initDone.current) {
            initDone.current = true
            ChatManager.init().then(() => setChatMode(ChatManager.getMode()))
        }
    }, [isOpen])

    // auto-scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }, [messages, loading])

    // speech recognition setup
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SpeechRecognition) return
        setSpeechSupported(true)
        const recognition = new SpeechRecognition()
        recognition.lang = SPEECH_LANGS[i18n.language] || 'de-DE'
        recognition.interimResults = true
        recognition.continuous = false
        recognition.onresult = (event) => {
            let transcript = ''
            for (let i = 0; i < event.results.length; i++) transcript += event.results[i][0].transcript
            setInput(transcript)
        }
        recognition.onend = () => setIsListening(false)
        recognition.onerror = (event) => {
            setIsListening(false)
            if (event.error === 'not-allowed') alert(t('chatbot.mic_denied'))
        }
        recognitionRef.current = recognition
        return () => recognition.abort()
    }, [i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    // build dashboard state snapshot for AI context (if dashboard is active)
    const buildDashboardState = useCallback(() => {
        const actions = ChatManager.getDashboardActions()
        if (!actions) return undefined
        return {
            active_tab: actions.getActiveTab(),
            date_range: actions.getDateRange(),
            detection_types_enabled: actions.getEnabledDetectionTypes(),
            detection_types_available: actions.getDetectionTypes(),
            weather_streams: actions.getWeatherStreams(),
            data_loaded: actions.getDataLoaded(),
            viewer_open: actions.getViewerDate() !== null,
            viewer_date: actions.getViewerDate()
        }
    }, [])

    // wait for dashboardActions to become available after navigation (polls every 200ms, max 15s)
    const waitForDashboardActions = useCallback(() => {
        return new Promise(resolve => {
            let elapsed = 0
            const id = setInterval(() => {
                const actions = ChatManager.getDashboardActions()
                if (actions) { clearInterval(id); resolve(actions) }
                elapsed += 200
                if (elapsed >= 15000) { clearInterval(id); resolve(null) }
            }, 200)
        })
    }, [])

    // execute dashboard actions, returns description strings
    const executeDashboardActions = useCallback((actions, dashboardActions) => {
        const descriptions = []
        const typeFilterAct = actions.find(a => a.action === 'show_only_detections')
        const allTypesAct = actions.find(a => a.action === 'show_all_detections')
        const setDatesAct = actions.find(a => a.action === 'set_dates')
        const typesOverride = setDatesAct && typeFilterAct ? typeFilterAct.types
            : setDatesAct && allTypesAct ? null : undefined

        for (const act of actions) {
            switch (act.action) {
                case 'show_only_detections':
                    if (!setDatesAct) dashboardActions.showOnlyDetections(act.types)
                    descriptions.push(t('chatbot.act_only') + ' ' + act.types.join(', '))
                    break
                case 'show_all_detections':
                    if (!setDatesAct) dashboardActions.showAllDetections()
                    descriptions.push(t('chatbot.act_all_types'))
                    break
                case 'set_dates':
                    dashboardActions.setDates(act.start, act.end, typesOverride)
                    descriptions.push(t('chatbot.act_dates'))
                    break
                case 'switch_tab':
                    dashboardActions.switchTab(act.tab)
                    descriptions.push(t('chatbot.act_tab') + ' ' + act.tab)
                    break
                case 'show_weather_streams':
                    dashboardActions.showWeatherStreams(act.streams)
                    descriptions.push(t('chatbot.act_streams') + ' ' + act.streams.join(', '))
                    break
                case 'reset_view':
                    dashboardActions.resetView()
                    descriptions.push(t('chatbot.act_reset'))
                    break
                case 'load_data':
                    descriptions.push(t('chatbot.act_loading'))
                    break
                case 'open_viewer':
                    dashboardActions.openViewer(act.date, act.timestamp || null, act.types || null)
                    descriptions.push(t('chatbot.act_viewer') + (act.date ? ' ' + t('chatbot.act_for') + ' ' + act.date : ''))
                    break
                default:
                    break
            }
        }
        return descriptions
    }, [t])

    // send message to AI
    const handleSend = useCallback(async () => {
        const text = input.trim()
        if (!text || loading) return

        setInput('')
        setMessages(prev => [...prev, { role: 'user', text }])
        setLoading(true)

        try {
            const json = await ChatManager.sendMessage(text, buildDashboardState())
            const newMsgs = [{ role: 'assistant', text: json.reply || t('chatbot.no_response') }]

            if (json.actions?.length) {
                let dashboardActions = ChatManager.getDashboardActions()

                // if dashboard is not active, navigate there and wait for it
                if (!dashboardActions) {
                    setMessages(prev => [...prev, ...newMsgs, { role: 'system', text: t('chatbot.act_navigating') }])
                    await ChatManager.navigateToDashboard()
                    dashboardActions = await waitForDashboardActions()
                }

                if (dashboardActions) {
                    const descriptions = executeDashboardActions(json.actions, dashboardActions)
                    if (descriptions.length) newMsgs.push({ role: 'system', text: t('chatbot.updated') + ' ' + descriptions.join('; ') })
                }
            }

            setMessages(prev => [...prev, ...newMsgs])
            setChatMode(ChatManager.getMode())
        } catch (e) {
            setMessages(prev => [...prev, { role: 'system', text: t('chatbot.error') }])
        } finally {
            setLoading(false)
        }
    }, [input, loading, buildDashboardState, waitForDashboardActions, executeDashboardActions, t])

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }

    const toggleListening = useCallback(() => {
        const recognition = recognitionRef.current
        if (!recognition) return
        if (isListening) { recognition.abort(); setIsListening(false) }
        else {
            setInput('')
            try { recognition.start(); setIsListening(true) } catch (e) { setIsListening(false) }
        }
    }, [isListening])

    const resetChat = () => {
        ChatManager.resetConversation()
        setMessages([{ role: 'assistant', text: t('chatbot.new_greeting') }])
    }

    // closed state: floating bubble
    if (!isOpen) {
        return (
            <Fab
                onClick={() => setIsOpen(true)}
                title={t('chatbot.title')}
                sx={{
                    position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
                    bgcolor: '#003366', color: 'white', width: 56, height: 56,
                    '&:hover': { bgcolor: '#004080' },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}
            >
                <AutoAwesome />
            </Fab>
        )
    }

    // open state: chat panel
    const modeBadge = chatMode !== 'mock' ? (
        <Box sx={{
            textAlign: 'center', py: 0.5, fontSize: 11, fontWeight: 600,
            color: 'white', bgcolor: chatMode === 'claude' ? '#7c3aed' : '#16a34a',
        }}>
            {chatMode === 'claude' ? t('chatbot.mode_claude') : t('chatbot.mode_openai')}
        </Box>
    ) : (
        <Box sx={{ textAlign: 'center', py: 0.5, fontSize: 11, color: '#999', bgcolor: '#f5f5f5' }}>
            {t('chatbot.mode_mock')}
        </Box>
    )

    return (
        <Paper
            elevation={8}
            sx={{
                position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
                width: 380, maxHeight: 520, minHeight: 320, display: 'flex', flexDirection: 'column',
                borderRadius: 3, overflow: 'hidden',
                animation: 'chatSlideUp 0.25s ease-out',
            }}
        >

            {/* header */}
            <Box sx={{
                bgcolor: '#003366', color: 'white', px: 2, 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                minHeight: 48,
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {t('chatbot.title')}
                </Typography>
                <Box>
                    <IconButton size="small" onClick={resetChat} title={t('chatbot.new')} sx={{ color: 'white' }}>
                        <Refresh fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setIsOpen(false)} title={t('chatbot.close')} sx={{ color: 'white' }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* mode badge */}
            {modeBadge}

            {/* messages */}
            <Box sx={{
                flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                minHeight: 0, maxHeight: 340,
                bgcolor: '#fafafa',
            }}>
                {messages.map((msg, i) => (
                    <Box key={i} sx={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : msg.role === 'system' ? 'center' : 'flex-start',
                        bgcolor: msg.role === 'user' ? '#003366' : msg.role === 'system' ? 'transparent' : '#f0f2f5',
                        color: msg.role === 'user' ? 'white' : msg.role === 'system' ? '#999' : '#2c3e50',
                        fontStyle: msg.role === 'system' ? 'italic' : 'normal',
                        fontSize: msg.role === 'system' ? 12 : 13,
                        px: msg.role === 'system' ? 1 : 1.5,
                        py: msg.role === 'system' ? 0.5 : 1,
                        borderRadius: 2,
                        maxWidth: msg.role === 'system' ? '100%' : '85%',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                    }}>
                        {msg.text}
                    </Box>
                ))}

                {/* typing indicator */}
                {loading && (
                    <Box sx={{ alignSelf: 'flex-start', display: 'flex', gap: 0.5, p: 1 }}>
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0s' }} />
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.15s' }} />
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.3s' }} />
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* input row */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, p: 1, borderTop: '1px solid #e0e0e0', bgcolor: 'white',
            }}>
                {speechSupported && (
                    <IconButton
                        size="small"
                        onClick={toggleListening}
                        disabled={loading}
                        sx={{
                            color: isListening ? 'white' : '#666',
                            bgcolor: isListening ? '#e53935' : 'transparent',
                            animation: isListening ? 'micPulse 1.5s ease-in-out infinite' : 'none',
                            '&:hover': { bgcolor: isListening ? '#c62828' : 'rgba(0,0,0,0.04)' },
                        }}
                        title={isListening ? t('chatbot.speech_stop') : t('chatbot.speech')}
                    >
                        {isListening ? <Stop fontSize="small" /> : <Mic fontSize="small" />}
                    </IconButton>
                )}
                <TextField
                    size="small"
                    fullWidth
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isListening ? t('chatbot.listening') : t('chatbot.placeholder')}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5, fontSize: 13 } }}
                />
                <IconButton
                    size="small"
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    sx={{
                        bgcolor: '#003366', color: 'white',
                        '&:hover': { bgcolor: '#004080' },
                        '&:disabled': { bgcolor: '#ccc', color: 'white' },
                    }}
                >
                    <Send fontSize="small" />
                </IconButton>
            </Box>
        </Paper>
    )
}
